import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CartContext } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currency';
import { LoadingState } from '../components/Loading';
import {
  FaShoppingCart,
  FaMinus,
  FaPlus,
  FaShieldAlt,
  FaShippingFast,
  FaUndoAlt,
  FaStar,
  FaArrowLeft,
} from 'react-icons/fa';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useContext(CartContext);
  const toast = useToast();
  const { settings } = useSettings();

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    setQuantity(1);
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const res = await axios.get(`/api/products/${id}`);
      setProduct(res.data);
      if (res.data?.category?._id) {
        loadRelated(res.data.category._id, res.data._id);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelated = async (categoryId, excludeId) => {
    try {
      const res = await axios.get(`/api/products?category=${categoryId}&limit=4`);
      setRelated(res.data.products.filter((p) => p._id !== excludeId).slice(0, 4));
    } catch (e) { console.error(e); }
  };

  const handleAddToCart = () => {
    if (product.stock < quantity) {
      toast.error('Insufficient stock');
      return;
    }
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart`);
  };

  const handleBuyNow = () => {
    if (product.stock < quantity) {
      toast.error('Insufficient stock');
      return;
    }
    addToCart(product, quantity);
    navigate('/cart');
  };

  if (loading) return <div className="page"><LoadingState message="Loading product..." /></div>;
  if (!product) {
    return (
      <div className="page">
        <div className="container">
          <div className="empty-state">
            <h3>Product not found</h3>
            <p>This product doesn't exist or has been removed.</p>
            <Link to="/products" className="btn btn-primary">Back to shop</Link>
          </div>
        </div>
      </div>
    );
  }

  const discount = product.comparePrice && product.comparePrice > product.price
    ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
    : 0;

  return (
    <div className="product-detail page">
      <div className="container">
        <Link to="/products" className="back-link">
          <FaArrowLeft /> Back to shop
        </Link>

        <div className="product-detail-grid">
          {/* Image gallery */}
          <div className="product-gallery">
            <div className="main-image">
              {product.images && product.images.length > 0 ? (
                <img src={product.images[activeImage].url} alt={product.name} />
              ) : (
                <div className="no-image">No Image</div>
              )}
              {discount > 0 && <span className="gallery-badge">-{discount}%</span>}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="thumbnails">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    className={`thumbnail ${i === activeImage ? 'active' : ''}`}
                    onClick={() => setActiveImage(i)}
                  >
                    <img src={img.url} alt={`${product.name} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="product-info-panel">
            {product.category?.name && (
              <span className="product-category">{product.category.name}</span>
            )}

            <h1>{product.name}</h1>

            {product.rating?.average > 0 && (
              <div className="rating-display">
                <span className="stars">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={i < Math.round(product.rating.average) ? 'filled' : 'empty'}
                    />
                  ))}
                </span>
                <span className="rating-text">
                  {product.rating.average.toFixed(1)} ({product.rating.count} reviews)
                </span>
              </div>
            )}

            <div className="price-display">
              <span className="current-price">{formatCurrency(product.price, settings?.currency)}</span>
              {product.comparePrice > product.price && (
                <>
                  <span className="original-price">{formatCurrency(product.comparePrice, settings?.currency)}</span>
                  <span className="savings-badge">Save {discount}%</span>
                </>
              )}
            </div>

            <p className="product-description">{product.description}</p>

            <div className="stock-display">
              {product.stock > 0 ? (
                <>
                  <span className="stock-dot in" />
                  <span>In stock — {product.stock} available</span>
                </>
              ) : (
                <>
                  <span className="stock-dot out" />
                  <span>Out of stock</span>
                </>
              )}
            </div>

            {product.stock > 0 && (
              <>
                <div className="quantity-row">
                  <label>Quantity</label>
                  <div className="quantity-stepper">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <FaMinus />
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={product.stock}
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) setQuantity(Math.min(Math.max(1, val), product.stock));
                      }}
                    />
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      disabled={quantity >= product.stock}
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>

                <div className="action-row">
                  <button onClick={handleAddToCart} className="btn btn-secondary btn-lg">
                    <FaShoppingCart /> Add to cart
                  </button>
                  <button onClick={handleBuyNow} className="btn btn-primary btn-lg">
                    Buy now
                  </button>
                </div>
              </>
            )}

            {/* Trust badges */}
            <div className="trust-badges">
              <div className="trust-badge">
                <FaShippingFast />
                <div>
                  <strong>Free shipping</strong>
                  <small>On orders over {formatCurrency(settings?.freeShippingThreshold || 50000, settings?.currency)}</small>
                </div>
              </div>
              <div className="trust-badge">
                <FaUndoAlt />
                <div>
                  <strong>30-day returns</strong>
                  <small>No questions asked</small>
                </div>
              </div>
              <div className="trust-badge">
                <FaShieldAlt />
                <div>
                  <strong>Secure checkout</strong>
                  <small>SSL encrypted payment</small>
                </div>
              </div>
            </div>

            {/* Specifications */}
            {product.specifications && product.specifications.length > 0 && (
              <div className="specs-section">
                <h3>Specifications</h3>
                <table className="specs-table">
                  <tbody>
                    {product.specifications.map((spec, i) => (
                      <tr key={i}>
                        <th>{spec.key}</th>
                        <td>{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="tags-section">
                {product.tags.map((tag) => (
                  <Link key={tag} to={`/products?search=${encodeURIComponent(tag)}`} className="tag">
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="related-section">
            <h2>You may also like</h2>
            <div className="related-grid">
              {related.map((item) => (
                <Link key={item._id} to={`/products/${item._id}`} className="product-card">
                  <div className="product-image">
                    {item.images && item.images[0] ? (
                      <img src={item.images[0].url} alt={item.name} loading="lazy" />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3>{item.name}</h3>
                    <span className="product-price">{formatCurrency(item.price, settings?.currency)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
